import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-bnpl-callback',
  templateUrl: './bnpl-callback.component.html',
  styleUrls: ['./bnpl-callback.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
  ],
})
export class BnplCallbackComponent implements OnInit, OnDestroy {
  transactionStatus:
    | 'loading'
    | 'success'
    | 'failure'
    | 'pending'
    | 'cancelled'
    | 'expired' = 'loading';
  transactionId: string | null = null;
  initialStatus: string | null = null;
  currentStore: any = null;
  retryCount = 0;
  maxRetries = 12; // 12 retries * 5 seconds = 1 minute max
  private retryTimeout: any;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    public router: Router
  ) {}

  ngOnInit() {
    // Get store from route params OR fallback to sessionStorage
    this.initializeStore();

    window.scrollTo(0, 0);

    // Extract reference and initial status from the URL
    this.transactionId = this.route.snapshot.queryParamMap.get('reference');
    this.initialStatus = this.route.snapshot.queryParamMap.get('status');

    if (this.transactionId) {
      this.verifyTransaction();
    } else {
      this.transactionStatus = 'failure';
    }
  }

  ngOnDestroy() {
    // Clear any pending retry timeouts
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private initializeStore() {
    // First try to get store from route params
    const storeNameFromRoute = this.route.snapshot.params['name'];

    if (storeNameFromRoute) {
      this.currentStore = { store_name: storeNameFromRoute };
    } else {
      // Fallback to sessionStorage
      try {
        const storedStore = JSON.parse(
          sessionStorage.getItem('currentStore') || 'null'
        );
        if (storedStore?.store_name) {
          this.currentStore = storedStore;
        } else {
        }
      } catch (error) {
        console.error('Error parsing store', error);
      }
    }
  }

  goBack() {
    if (this.currentStore?.store_name) {
      this.router.navigate(['stores', this.currentStore.store_name]);
    } else {
      // Fallback to home page if no store context
      this.router.navigate(['/']);
    }
  }

  verifyTransaction() {
    // Prevent excessive retries
    if (this.retryCount >= this.maxRetries) {
      console.error('Max retries reached for transaction verification');
      this.transactionStatus = 'failure';
      return;
    }

    const verifyTransactionUrl = `${environment.BASE_URL}/storefront/api/v1/stanbicbnpl/verify/${this.transactionId}`;

    this.http
      .get<{ status: string; message?: string }>(verifyTransactionUrl)
      .subscribe({
        next: (response) => {
          // Map backend response status to frontend UI
          switch (response.status.toLowerCase()) {
            case 'success':
            case 'successful':
              this.transactionStatus = 'success';
              // Clear cart on successful payment
              this.clearCartOnSuccess();
              break;
            case 'failure':
            case 'failed':
              this.transactionStatus = 'failure';
              break;
            case 'pending':
              this.transactionStatus = 'pending';
              this.retryCount++;

              // Retry after 5 seconds
              this.retryTimeout = setTimeout(() => {
                this.verifyTransaction();
              }, 5000);
              break;
            case 'cancelled':
              this.transactionStatus = 'cancelled';
              break;
            case 'expired':
              this.transactionStatus = 'expired';
              break;
            default:
              console.warn('Unknown transaction status:', response.status);
              this.transactionStatus = 'failure';
              break;
          }
        },
        error: (error) => {
          console.error('Error verifying transaction:', error);

          // If it's a network error and we haven't exceeded retries, try again
          if (
            this.retryCount < this.maxRetries &&
            (error.status === 0 || error.status >= 500)
          ) {
            this.retryCount++;

            this.retryTimeout = setTimeout(() => {
              this.verifyTransaction();
            }, 5000);
          } else {
            this.transactionStatus = 'failure';
          }
        },
      });
  }

  private clearCartOnSuccess() {
    // Clear cart data on successful payment
    try {
      if (this.currentStore?.store_code) {
        // Use the same cart key pattern as your main component
        localStorage.removeItem(`cart_${this.currentStore.store_code}`);
        localStorage.removeItem(`cartTotal_${this.currentStore.store_code}`);
      }

      // Also clear any generic cart data
      localStorage.removeItem('cart');
      localStorage.removeItem('cartTotal');
    } catch (error) {}
  }

  // Helper method to get user-friendly status text
  getStatusText(): string {
    switch (this.transactionStatus) {
      case 'loading':
        return 'Verifying transaction...';
      case 'success':
        return 'Payment Successful!';
      case 'failure':
        return 'Payment Failed!';
      case 'pending':
        return `Payment Pending... (Attempt ${this.retryCount}/${this.maxRetries})`;
      case 'cancelled':
        return 'Transaction Cancelled';
      case 'expired':
        return 'Transaction Expired';
      default:
        return 'Unknown Status';
    }
  }

  // Helper method to get appropriate button text
  getButtonText(): string {
    switch (this.transactionStatus) {
      case 'success':
        return 'Continue Shopping';
      case 'failure':
      case 'cancelled':
      case 'expired':
        return 'Try Again';
      default:
        return 'Go to Store';
    }
  }
}
